/**
 * NotchesAutomatedTradingPage — /n2/automated-trading
 *
 * Explains what "Automated Trading Engines" are — dedicated engines that
 * place trades at the customer's broker on their behalf, subject to
 * per-engine rules and kill switches.
 *
 * Three families exist today (audited 2026-09-13):
 *   1. Alpaca — Stocks    ($49.99/mo, standalone; existing productConfig.algo)
 *   2. Tastytrade — Stocks (admin-gated in createStockEngine.js; not publicly
 *      sold today — surfaced here as "In preview")
 *   3. Tastytrade — Options (new Starter/Standard/Pro engine-count tiers per
 *      docs/wiki/decisions/2026-09-12-notches-auto-trader-engine-tiers.md)
 *
 * Business bullets per family were derived from the actual engine-config
 * form fields in createEngine.js / createStockEngine.js / createOptionsEngine.js,
 * not invented. Kept to the material choices a customer would compare on —
 * dropped cosmetic + advanced-DSL knobs.
 *
 * This page is a public explainer, not the configuration surface. Configure
 * links route into /algo/engines and /options-trader/engines; TT Stocks has
 * no public config link while admin-gated.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { palette, type as typeTokens } from './theme';
import { NotchesExplainerFrame, useIsMobile, useReducedMotion } from './NotchesExplainerFrame';

type Tier = {
  name: string;
  cap: string;
  monthly: number;
  yearly: number;
};

type Pricing =
  | { kind: 'flat'; monthly: number; yearly: number }
  | { kind: 'tiered'; tiers: Tier[] }
  | { kind: 'preview'; note: string };

type Broker = {
  name: string;
  monogram: string; // 1-2 char badge label
  url: string;      // public broker site
};

const BROKER_TASTYTRADE: Broker = {
  name: 'Tastytrade',
  monogram: 'tt',
  url: 'https://tastytrade.com',
};
const BROKER_ALPACA: Broker = {
  name: 'Alpaca',
  monogram: 'a',
  url: 'https://alpaca.markets',
};

type EngineFamily = {
  key: string;
  name: string;
  broker: Broker;
  description: string;
  runsWhat: string[];
  highlights: string[];
  pricing: Pricing;
  status: 'live' | 'preview';
  destination: string | null;
  destinationLabel: string;
};

// Order: TT Options first (headline product with the new tier ladder),
// then Alpaca Stocks (public standalone), then TT Stocks (in preview).
// Buy-now first, preview last — matches the comparison table column order.
const ENGINE_FAMILIES: EngineFamily[] = [
  {
    key: 'options-tastytrade',
    name: 'Options Auto-Trader',
    broker: BROKER_TASTYTRADE,
    description:
      'Runs multi-leg options trades on your Tastytrade brokerage account using the Notches Researched Opportunities feed. Per-symbol engines with regime-gated entries and strategy-aware exits.',
    runsWhat: [
      'Six symbols supported: ES, MES, SPX, SPY, GC, CL',
      'Four strategies: naked put · bear call spread · jade lizard · iron condor',
      'One strategy per engine — combine engines to cover multiple',
    ],
    highlights: [
      'Paper mode or live trading',
      'Auto-close the trade OR alerts only',
      'Trade type: credit spread, naked, or auto-select',
      'Preset stance: Conservative, Balanced, or Aggressive',
      'Smart profit close OR strict target',
      'Convert-to-spread on breach (an alternative to a hard stop)',
      'Optional underlying hard stop',
      'Pick your horizon strategy (lowest DTE / highest score / highest credit / best theta)',
      'Choose which Tastytrade account each engine runs on',
    ],
    pricing: {
      kind: 'tiered',
      tiers: [
        { name: 'Starter', cap: '1 engine', monthly: 49.99, yearly: 499.99 },
        { name: 'Standard', cap: 'up to 5 engines', monthly: 174.99, yearly: 1749.99 },
        { name: 'Pro', cap: 'up to 10 engines', monthly: 299.99, yearly: 2999.99 },
      ],
    },
    status: 'live',
    destination: '/options-trader/engines',
    destinationLabel: 'Configure Options Engines →',
  },
  {
    key: 'stocks-alpaca',
    name: 'Stock Auto-Trader',
    broker: BROKER_ALPACA,
    description:
      'Runs stock trades on your Alpaca brokerage account using your own symbols and rules. Optional AI evaluation from Notches decides whether each candidate is worth the trade.',
    runsWhat: [
      'Up to 5 symbols per policy — your list, not a fixed universe',
      'Ten rule strategies (momentum, breakout, bounce, trend + bearish mirrors) or a custom rule set',
      'Intraday cadence — 15, 30, or 60 minute checks',
    ],
    highlights: [
      'Paper mode or live trading',
      'Auto-execute the trade OR receive an alert only',
      'AI evaluation: Off, Advisory, or Strict',
      'Set your own AI-confidence bar (25–100%) before an engine acts',
      'Bring your own symbols — no pre-set universe',
      'Five risk levels (conservative → max-risk)',
      'Multiple named policies inside one engine',
    ],
    pricing: { kind: 'flat', monthly: 49.99, yearly: 499.9 },
    status: 'live',
    destination: '/algo/engines',
    destinationLabel: 'Configure Stock Engines →',
  },
  {
    key: 'stocks-tastytrade',
    name: 'Stock Auto-Trader',
    broker: BROKER_TASTYTRADE,
    description:
      'Runs stock trades on your Tastytrade brokerage account. Follows Notches Researched Opportunities, a rule-based strategy, or your own symbol list — with a macro regime overlay you can toggle.',
    runsWhat: [
      'Follow Notches Researched Opportunities, pick a rule strategy, or bring your own symbols',
      'Three curated universes (Mega-Cap Tech · SPDR Sectors · Broad ETFs) or up to 100 of your own',
      'Daily cadence',
    ],
    highlights: [
      'Paper mode or live trading',
      'Follow Researched Opportunities OR rule-based strategy OR your own symbols',
      'Curated universe or custom list',
      'Macro regime overlay — on or off',
      'Regular hours or extended hours',
      'Three risk levels',
      'Choose which Tastytrade account each engine runs on',
    ],
    pricing: {
      kind: 'preview',
      note: 'In private preview. Reach out if you want early access.',
    },
    status: 'preview',
    destination: null,
    destinationLabel: 'In preview',
  },
];

// Cross-family comparison — one row per material business option, columns
// per family. Column order matches ENGINE_FAMILIES: TT Options first,
// Alpaca second, TT Stocks third. All values sourced from the audit;
// don't add rows without confirming against the create-engine Lambda.
type ComparisonRow = { label: string; ttOptions: string; alpaca: string; ttStocks: string };
const COMPARISON: ComparisonRow[] = [
  { label: 'Paper or live',                ttOptions: 'Yes',                    alpaca: 'Yes',              ttStocks: 'Yes' },
  { label: 'Auto-execute or alert-only',   ttOptions: 'Auto or Alert',          alpaca: 'Toggle',           ttStocks: 'Auto only' },
  { label: 'AI evaluation controls',       ttOptions: 'Built into researched opportunities',     alpaca: 'Off / Advisory / Strict', ttStocks: '—' },
  { label: 'Follow Researched Opportunities',    ttOptions: 'Default',                alpaca: '—',                ttStocks: 'Yes' },
  { label: 'Bring your own symbols',       ttOptions: '—',                      alpaca: 'Up to 5',          ttStocks: 'Up to 100' },
  { label: 'Curated universes',            ttOptions: 'N/A',                    alpaca: '—',                ttStocks: '3 presets' },
  { label: 'Rule-based strategies',        ttOptions: 'N/A',                    alpaca: '10 built-in + custom rules',  ttStocks: '9 built-in' },
  { label: 'Macro regime overlay',         ttOptions: 'Built-in',               alpaca: '—',                ttStocks: 'Toggle' },
  { label: 'Multiple policies per engine', ttOptions: 'No — one strategy per engine', alpaca: 'Yes',        ttStocks: 'Yes' },
  { label: 'Checking frequency',           ttOptions: 'Scheduled checks',       alpaca: '15/30/60 min',     ttStocks: 'Daily' },
  { label: 'Convert-to-spread exit',       ttOptions: 'Yes',                    alpaca: 'N/A',              ttStocks: 'N/A' },
];

export const NotchesAutomatedTradingPage: React.FC = () => {
  const isMobile = useIsMobile();
  const reduced = useReducedMotion();

  return (
    <NotchesExplainerFrame
      breadcrumbTrail={[
        { label: 'Notches', to: '/n2' },
        { label: 'Execute', to: '/n2?path=execute' },
        { label: 'Automated Trading' },
      ]}
    >
      <Hero reduced={reduced} isMobile={isMobile} />

      <Section title="How an automated engine works">
        <p style={paragraphStyle}>
          An engine sits between the market and your broker. You choose what
          it can trade, how it should evaluate each opportunity, and whether
          it should execute or just alert you. It then watches the position
          and closes it when the exit rule triggers — or steps aside if a
          kill switch fires.
        </p>
        <ol style={orderedListStyle}>
          <li>
            <strong>Connect</strong> — link your broker (Alpaca or Tastytrade)
            with API keys you generate and store yourself.
          </li>
          <li>
            <strong>Configure</strong> — pick a family below, set the
            settings that fit your account, and start in paper mode.
          </li>
          <li>
            <strong>Verify</strong> — watch it run in paper. When you're
            satisfied, flip the engine to live.
          </li>
          <li>
            <strong>Monitor</strong> — every engine has a kill switch and a
            position monitor. You can stop it at any time.
          </li>
        </ol>
      </Section>

      <Section title="Three engine families">
        <p style={paragraphStyle}>
          Each family runs on a specific broker and has its own set of
          material choices. Pick the family that matches how you want to
          trade — the comparison table below shows the differences at a
          glance.
        </p>
        <ul
          style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 20,
          }}
        >
          {ENGINE_FAMILIES.map((f) => (
            <EngineFamilyCard key={f.key} family={f} />
          ))}
        </ul>
      </Section>

      <Section title="Compare the families">
        <ComparisonTable rows={COMPARISON} isMobile={isMobile} />
      </Section>

      <Section title="How to subscribe">
        <p style={paragraphStyle}>
          The Stock Auto-Trader on Alpaca is a standalone subscription. The
          Options Auto-Trader on Tastytrade uses engine-count tiers — pay for
          the number of engines you run, from a single engine on Starter up
          to ten on Pro. The Stock Auto-Trader on Tastytrade is in private
          preview. See{' '}
          <Link to="/n2/plans" style={inlineLinkStyle}>
            all plans and pricing
          </Link>
          .
        </p>
      </Section>

      <Section title="Where to next">
        <div style={ctaRowStyle(isMobile)}>
          <CtaLink to="/options-trader/engines" primary>
            Configure Options Engines (Tastytrade) →
          </CtaLink>
          <CtaLink to="/algo/engines">
            Configure Stock Engines (Alpaca) →
          </CtaLink>
          <CtaLink to="/upgrade">See all plans →</CtaLink>
          <CtaLink to="/n2/manual-trading">
            Or: Manual Trading →
          </CtaLink>
        </div>
      </Section>
    </NotchesExplainerFrame>
  );
};

// ─── Building blocks ─────────────────────────────────────────────────────

const Hero: React.FC<{ reduced: boolean; isMobile: boolean }> = ({
  reduced,
  isMobile,
}) => (
  <header
    style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
      animation: reduced
        ? undefined
        : 'notches-explainer-fade-in 320ms ease-out both',
    }}
  >
    <span
      style={{
        fontFamily: typeTokens.sans,
        fontSize: 12,
        fontWeight: 700,
        color: palette.ink3,
        letterSpacing: '0.2em',
        textTransform: 'uppercase',
      }}
    >
      Execute · Automated Trading
    </span>
    <h1
      tabIndex={-1}
      style={{
        fontFamily: typeTokens.serif,
        fontSize: isMobile ? 32 : 40,
        fontWeight: 400,
        margin: 0,
        color: palette.ink,
        letterSpacing: '-0.01em',
        lineHeight: 1.15,
        textWrap: 'balance' as any,
        outline: 'none',
      }}
    >
      Put supported trading strategies into practice.
    </h1>
    <p
      style={{
        fontFamily: typeTokens.sans,
        fontSize: 16,
        color: palette.ink2,
        lineHeight: 1.5,
        margin: 0,
        maxWidth: 620,
      }}
    >
      Automated Trading Engines are dedicated Notches engines for supported
      systematic strategies across stocks and options. You connect your
      broker, pick the settings that fit your account — Researched
      Opportunities or your own symbols, auto-execute or alert-only,
      AI-evaluated or rules-based — and the engine handles entries, exits,
      and safety guards you configure. No control guarantees fills or
      protection from loss.
    </p>
  </header>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <section style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
    <h2
      style={{
        fontFamily: typeTokens.serif,
        fontSize: 24,
        fontWeight: 400,
        margin: 0,
        color: palette.ink,
        letterSpacing: '-0.005em',
        lineHeight: 1.2,
      }}
    >
      {title}
    </h2>
    {children}
  </section>
);

const EngineFamilyCard: React.FC<{ family: EngineFamily }> = ({ family }) => {
  const { name, broker, description, runsWhat, highlights, pricing, status, destination, destinationLabel } =
    family;
  return (
    <li
      style={{
        border: `1px solid ${palette.line}`,
        borderRadius: 2,
        padding: 20,
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        background: palette.paper,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 20,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
          <BrokerBadge broker={broker} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
            <h3
              style={{
                fontFamily: typeTokens.serif,
                fontSize: 22,
                fontWeight: 400,
                margin: 0,
                color: palette.ink,
                lineHeight: 1.2,
              }}
            >
              {name}
            </h3>
            <span
              style={{
                fontFamily: typeTokens.sans,
                fontSize: 12,
                fontWeight: 700,
                color: palette.ink3,
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
              }}
            >
              Broker ·{' '}
              <a
                href={broker.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: palette.goldDeep,
                  textDecoration: 'underline',
                  textDecorationColor: palette.line,
                  textUnderlineOffset: 3,
                }}
              >
                {broker.name} ↗
              </a>
            </span>
          </div>
        </div>
        <StatusPill status={status} />
      </div>

      <p style={{ ...paragraphStyle, maxWidth: 640, margin: 0 }}>{description}</p>

      <BulletBlock label="Choose from" items={highlights} accent />
      <BulletBlock label="What it trades" items={runsWhat} />

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 20,
          flexWrap: 'wrap',
          borderTop: `1px solid ${palette.line}`,
          paddingTop: 14,
        }}
      >
        <PriceBlock pricing={pricing} />
        {destination ? (
          <Link to={destination} style={inlineLinkStyle}>
            {destinationLabel}
          </Link>
        ) : (
          <span
            style={{
              fontFamily: typeTokens.sans,
              fontSize: 13,
              color: palette.ink3,
              fontStyle: 'italic',
            }}
          >
            {destinationLabel}
          </span>
        )}
      </div>
    </li>
  );
};

// Small monogram badge — a tasteful placeholder for a broker logo. Using a
// text monogram in the site's own palette avoids trademark-adjacent
// hotlinking to broker artwork; the broker name next to it is the actual
// link out to their site.
const BrokerBadge: React.FC<{ broker: Broker }> = ({ broker }) => (
  <span
    aria-hidden="true"
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 44,
      height: 44,
      borderRadius: 6,
      border: `1.25px solid ${palette.gold}`,
      background: palette.paper2,
      color: palette.goldDeep,
      fontFamily: typeTokens.serif,
      fontSize: 20,
      fontWeight: 500,
      letterSpacing: '-0.02em',
      lineHeight: 1,
      flexShrink: 0,
    }}
  >
    {broker.monogram}
  </span>
);

const BulletBlock: React.FC<{
  label: string;
  items: string[];
  accent?: boolean;
}> = ({ label, items, accent }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
    <span
      style={{
        fontFamily: typeTokens.sans,
        fontSize: 12,
        fontWeight: 700,
        color: accent ? palette.goldDeep : palette.ink3,
        letterSpacing: '0.16em',
        textTransform: 'uppercase',
      }}
    >
      {label}
    </span>
    <ul
      style={{
        listStyle: 'disc',
        paddingLeft: 20,
        margin: 0,
        color: palette.ink2,
        fontFamily: typeTokens.sans,
        fontSize: 14,
        lineHeight: 1.55,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      {items.map((s) => (
        <li key={s}>{s}</li>
      ))}
    </ul>
  </div>
);

const StatusPill: React.FC<{ status: 'live' | 'preview' }> = ({ status }) => (
  <span
    style={{
      fontFamily: typeTokens.sans,
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '0.18em',
      textTransform: 'uppercase',
      color: status === 'live' ? palette.positive : palette.caution,
      border: `1px solid ${status === 'live' ? palette.positive : palette.caution}`,
      padding: '4px 10px',
      borderRadius: 999,
    }}
  >
    {status === 'live' ? 'Available' : 'In preview'}
  </span>
);

const PriceBlock: React.FC<{ pricing: Pricing }> = ({ pricing }) => {
  if (pricing.kind === 'preview') {
    return (
      <span
        style={{
          fontFamily: typeTokens.sans,
          fontSize: 14,
          color: palette.ink2,
          lineHeight: 1.4,
          maxWidth: 360,
        }}
      >
        {pricing.note}
      </span>
    );
  }
  if (pricing.kind === 'flat') {
    const yearlyPerMonth = pricing.yearly / 12;
    return (
      <div style={{ fontFamily: typeTokens.sans, fontSize: 14, color: palette.ink2 }}>
        <div style={{ color: palette.ink }}>
          <strong>${pricing.monthly.toFixed(2)}</strong> / mo
        </div>
        <div style={{ color: palette.ink3, fontSize: 12 }}>
          ${pricing.yearly.toFixed(2)} / yr — ${yearlyPerMonth.toFixed(2)}/mo
        </div>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span
        style={{
          fontFamily: typeTokens.sans,
          fontSize: 12,
          fontWeight: 700,
          color: palette.ink3,
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
        }}
      >
        Engine-count tiers
      </span>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'auto 1fr auto',
          gap: '4px 12px',
          alignItems: 'baseline',
          fontFamily: typeTokens.sans,
          fontSize: 14,
        }}
      >
        {pricing.tiers.map((t) => (
          <React.Fragment key={t.name}>
            <span style={{ color: palette.ink, fontWeight: 600 }}>{t.name}</span>
            <span style={{ color: palette.ink3, fontSize: 12 }}>{t.cap}</span>
            <span style={{ color: palette.ink }}>
              <strong>${t.monthly.toFixed(2)}</strong>
              <span style={{ color: palette.ink3, marginLeft: 4 }}>/ mo</span>
            </span>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

const ComparisonTable: React.FC<{
  rows: ComparisonRow[];
  isMobile: boolean;
}> = ({ rows, isMobile }) => {
  if (isMobile) {
    // On narrow screens, wide tables scroll horizontally — that's better
    // than truncating labels. Keep the overflow container so the page body
    // itself never scrolls sideways.
    return (
      <div style={{ overflowX: 'auto', width: '100%' }}>
        <TableInner rows={rows} />
      </div>
    );
  }
  return <TableInner rows={rows} />;
};

const TableInner: React.FC<{ rows: ComparisonRow[] }> = ({ rows }) => (
  <table
    style={{
      width: '100%',
      minWidth: 560,
      borderCollapse: 'collapse',
      fontFamily: typeTokens.sans,
      fontSize: 14,
      color: palette.ink2,
    }}
  >
    <thead>
      <tr style={{ borderBottom: `1px solid ${palette.line}` }}>
        <th style={thStyle}>Option</th>
        <th style={thStyle}>Tastytrade · Options</th>
        <th style={thStyle}>Alpaca · Stocks</th>
        <th style={thStyle}>Tastytrade · Stocks</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((r) => (
        <tr key={r.label} style={{ borderBottom: `1px solid ${palette.line}` }}>
          <td style={tdLabelStyle}>{r.label}</td>
          <td style={tdStyle}>{r.ttOptions}</td>
          <td style={tdStyle}>{r.alpaca}</td>
          <td style={tdStyle}>{r.ttStocks}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  fontFamily: typeTokens.sans,
  fontSize: 12,
  fontWeight: 700,
  color: palette.ink3,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  padding: '10px 12px',
  verticalAlign: 'bottom',
};

const tdStyle: React.CSSProperties = {
  padding: '12px',
  color: palette.ink2,
  lineHeight: 1.4,
  verticalAlign: 'top',
};

const tdLabelStyle: React.CSSProperties = {
  ...tdStyle,
  color: palette.ink,
  fontWeight: 500,
};

const CtaLink: React.FC<{
  to: string;
  primary?: boolean;
  children: React.ReactNode;
}> = ({ to, primary, children }) => (
  <Link
    to={to}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 8,
      padding: '10px 16px',
      borderRadius: 2,
      textDecoration: 'none',
      fontFamily: typeTokens.sans,
      fontSize: 14,
      fontWeight: 600,
      color: primary ? palette.paper : palette.ink,
      background: primary ? palette.goldDeep : 'transparent',
      border: `1px solid ${primary ? palette.goldDeep : palette.line}`,
      transition: 'background 150ms ease-out, color 150ms ease-out',
    }}
  >
    {children}
  </Link>
);

const paragraphStyle: React.CSSProperties = {
  fontFamily: typeTokens.sans,
  fontSize: 15,
  color: palette.ink2,
  lineHeight: 1.6,
  margin: 0,
  maxWidth: 640,
};

const orderedListStyle: React.CSSProperties = {
  fontFamily: typeTokens.sans,
  fontSize: 15,
  color: palette.ink2,
  lineHeight: 1.65,
  margin: 0,
  paddingLeft: 22,
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  maxWidth: 640,
};

const inlineLinkStyle: React.CSSProperties = {
  color: palette.goldDeep,
  textDecoration: 'underline',
  textDecorationColor: palette.line,
  textUnderlineOffset: 3,
  fontWeight: 600,
};

const ctaRowStyle = (isMobile: boolean): React.CSSProperties => ({
  display: 'flex',
  flexDirection: isMobile ? 'column' : 'row',
  gap: 12,
  flexWrap: 'wrap',
});
